import { Component, computed, effect, inject, OnInit, signal, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, FormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, Subscription, catchError, debounceTime, distinctUntilChanged, finalize, map, of } from 'rxjs';

import { AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { TranslateService } from '@ngx-translate/core';
import { Translation } from '../../../models/translation';
import { LotItemProxyService } from '../../../proxies/inventory/lot-item.proxy.service';
import { LotItem, SplitLotItem } from '../../../models/inventory/lot-item';
import { LotItemSearchCommand } from '../../../models/inventory/lot-item-search';
import { MessageService } from 'primeng/api';
import { EnumService } from '../../../services/enum.service';
import { EnumValues } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import _ from 'lodash';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { ImportantLocations, LocationNode } from '../../../models/location';
import { LocationProxyService } from '../../../proxies/inventory/location.proxy.service';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { ItemMaster } from '../../../models/item/item-master';
import { TranslationLangKey } from '../../../models/translation/Translations';

@Component({
  selector: 'app-split-lot-items-detail',
  templateUrl: './split-lot-items-detail.component.html',
  styleUrl: './split-lot-items-detail.component.scss',
})
export class SplitLotItemsDetailComponent implements OnInit, OnDestroy {
  packagingTypes: EnumValues = {};
  packagingTypeOptions: { label: string; value: string }[] = [];
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private formBuilder = inject(FormBuilder);
  private valueChangeSubscriptions: Subscription[] = [];
  private readonly messageService = inject(MessageService);
  readonly lotItemProxyService = inject(LotItemProxyService);
  private readonly locationProxyService = inject(LocationProxyService);
  private readonly itemMasterProxyService = inject(ItemMasterProxyService);

  isLoading = signal(0);
  splitLotItemId = signal<string | undefined>('');
  existingSplitLotItem = signal<LotItem[]>([]);

  lotItemSearch = new FormControl<LotItem | null>(null);
  selectedLotItem = signal<LotItem | null>(null);
  selectedItemMaster = signal<ItemMaster | null>(null);
  mainForm: FormGroup;
  currentLotItemSelected = '';
  splitItemsForms = signal<FormGroup[]>([]);
  private formValidationState = signal(0);
  locations: LocationNode[] | null = null;
  private unitOfMeasureValues: Record<string, string> = {};

  splitItemsValues = signal<SplitLotItem[]>([]);

  searchTerm = toSignal(this.lotItemSearch.valueChanges.pipe(debounceTime(300), distinctUntilChanged()), { initialValue: null });

  private readonly translations = signal({
    Details: { es: 'Detalles', en: 'Details' },
    Warning: { es: 'Advertencia', en: 'Warning' },
    NotSplitIfNotReceived: {
      es: 'No se puede dividir un lote que aún no ha sido recibido',
      en: 'Cannot split a lot that has not been received yet',
    },
    ExceedsOriginalQuantity: {
      es: 'Las cantidades ingresadas superan la cantidad disponible del lote original',
      en: 'The entered quantities exceed the available quantity of the original lot',
    },
  });

  summaryInfo = computed(() => {
    const lotItem = this.selectedLotItem();

    if (!lotItem) {
      return {
        manufacturerLabelId: '',
        provider: '',
        manufacturer: '',
        manufacturerLotNumber: '',
        originalGrossQuantity: '',
        originalNetQuantity: '',
        currentNetQuantity: '',
        currentGrossQuantity: '',
      };
    }

    return {
      manufacturerLabelId: lotItem.providerLabelNumber || 'N/A',
      provider: lotItem.provider?.name || 'N/A',
      manufacturer: lotItem.manufacturer?.name || 'N/A',
      manufacturerLotNumber: lotItem.providerLotId || 'N/A',
      originalGrossQuantity: lotItem.originalGrossQuantity?.toString() || lotItem.grossQuantity?.toString() || '0',
      originalNetQuantity: lotItem.originalNetQuantity?.toString() || lotItem.netQuantity?.toString() || '0',
      currentNetQuantity: lotItem.netQuantity?.toString() || 'N/A',
      currentGrossQuantity: lotItem.grossQuantity?.toString() || 'N/A',
    };
  });

  uomLabel = computed(() => {
    const item = this.selectedItemMaster();
    if (!item) return '';
    return this.unitOfMeasureValues[String(item.unitOfMeasure)] || '';
  });

  itemDisplayInfo = computed(() => {
    const item = this.selectedItemMaster();
    if (!item) return '';
    const lang = (this.translateService.currentLang || 'en') as TranslationLangKey;
    const name = item.itemDisplayName?.[lang] || item.itemDisplayName?.en || item.itemLongDisplayName || '';
    return name ? `(${item.itemCode}) ${name}` : item.itemCode;
  });

  hasDataInTable = computed(() => {
    return this.splitItemsForms().length > 0;
  });

  totalGrossQuantity = computed(() => {
    return this.splitItemsValues().reduce(
      (sum: number, item: SplitLotItem) => sum + (parseFloat(String(item.originalGrossQuantity)) || 0),
      0,
    );
  });

  totalNetQuantity = computed(() => {
    return this.splitItemsValues().reduce(
      (sum: number, item: SplitLotItem) => sum + (parseFloat(String(item.originalNetQuantity)) || 0),
      0,
    );
  });

  remainingQuantity = computed(() => {
    const original = this.selectedLotItem()?.netQuantity || 0;
    const used = this.totalNetQuantity();
    return original - used;
  });

  canSave = computed(() => {
    this.formValidationState();

    const hasSelectedItem = this.selectedLotItem() !== null;
    const hasValidForms = this.splitItemsForms().every((form) => form.valid);
    const hasItems = this.splitItemsForms().length > 0;

    return hasSelectedItem && hasValidForms && hasItems;
  });

  constructor(private readonly enumService: EnumService) {
    this.splitLotItemId.set(this.route.snapshot.queryParamMap.get('id') || '');

    this.mainForm = this.createMainForm();
    this.setupFormValueSync();
  }

  private setupFormValueSync() {
    effect(
      () => {
        const forms = this.splitItemsForms();
        const initialValues = forms.map((form) => form.value);
        this.splitItemsValues.set(initialValues);
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const forms = this.splitItemsForms();
        this.cleanupSubscriptions();

        this.valueChangeSubscriptions = forms.flatMap((form) => [
          form.valueChanges.subscribe(() => {
            const currentValues = this.splitItemsForms().map((f) => f.value);
            this.splitItemsValues.set(currentValues);
            this.formValidationState.update((v) => v + 1);
          }),
          form.statusChanges.subscribe(() => {
            this.formValidationState.update((v) => v + 1);
          }),
        ]);
      },
      {
        allowSignalWrites: true,
      },
    );
  }

  ngOnInit(): void {
    const packagingTypes = this.enumService.getEnum('PackagingType', 'inventory').values;
    this.packagingTypeOptions = this._setOptionsForPrimeNg(packagingTypes as Record<string, string>);
    this.unitOfMeasureValues = this.enumService.getEnum('UnitOfMeasure', 'item').values as Record<string, string>;

    this.isLoading.update((val) => val + 1);
    this.locationProxyService
      .batchGetByCodes([
        ImportantLocations.RAW_MATERIAL,
        ImportantLocations.INITIAL_VIRTUAL,
        ImportantLocations.PC_RAW_MATERIAL,
        ImportantLocations.PC_FINISHED_GOODS,
      ])
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.locations = response.data;
        },
      });
  }

  startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  stopLoading(): void {
    this.isLoading.update((val) => Math.max(0, val - 1));
  }

  cancel() {
    this.router.navigate(['/split-lot-items']);
  }

  private async loadExistingSplitLotItem(request: string[]) {
    this.lotItemProxyService
      .batchLotItem(request)
      .pipe(
        finalize(() => {
          this.stopLoading();
        }),
      )
      .subscribe({
        next: (response) => {
          console.log('Lot items existing:', response.data);
          if (response.data) {
            this.existingSplitLotItem.set(response.data);
          }
        },
      });
  }

  public fetchLotItems = (query: string): Observable<LotItem[]> => {
    if (!query) return of([]);

    this.startLoading();

    const request: LotItemSearchCommand = {
      lotItemNumbers: [query],
      locationPaths: this.locations ? this.locations.map((loc) => loc.path!) : [],
    };

    return this.lotItemProxyService.search(request).pipe(
      finalize(() => {
        this.stopLoading();
      }),
      map((response) => (response.data?.items ?? []) as unknown as LotItem[]),
      catchError(() => of([] as LotItem[])),
    );
  };

  onLotItemSelected(event?: AutoCompleteSelectEvent) {
    const currentLang = this.translateService.currentLang || 'en';
    const translations = this.translations();

    if (event && event.value) {
      this.startLoading();
      this.lotItemProxyService
        .getByLotItemNumber(event.value.number || '')
        .pipe(finalize(() => this.stopLoading()))
        .subscribe({
          next: (response) => {
            if (!response.data?.dateReceived) {
              this.messageService.add({
                severity: 'warn',
                summary: translations.Warning[currentLang as keyof Translation],
                detail: translations.NotSplitIfNotReceived[currentLang as keyof Translation],
                life: 3000,
              });

              this.splitLotItemId.set(undefined);
              this.selectedLotItem.set(null);
              return;
            }

            if (!response.data.id) {
              this.splitLotItemId.set(undefined);
              this.selectedLotItem.set(null);
              return;
            }

            this.currentLotItemSelected = response.data.id;
            this.getLotItemById(response.data.id);
          },
          error: () => {
            this.splitLotItemId.set(undefined);
            this.selectedLotItem.set(null);
          },
        });
    } else {
      this.currentLotItemSelected = '';
      this.splitLotItemId.set(undefined);
      this.selectedLotItem.set(null);
      this.selectedItemMaster.set(null);
      this.existingSplitLotItem.set([]);
    }
    if (this.splitItemsForms().length > 0) {
      this.splitItemsForms.set([]);
    }
  }

  createMainForm(): FormGroup {
    return this.formBuilder.group({
      selectedLotItemId: ['', Validators.required],
      splitItems: this.formBuilder.array([]),
    });
  }

  createSplitItemForm(initialData?: Partial<SplitLotItem>): FormGroup {
    const form = this.formBuilder.group({
      id: [initialData?.id || ``],
      originalGrossQuantity: [initialData?.originalGrossQuantity || 0],
      originalNetQuantity: [
        initialData?.originalNetQuantity || 0,
        [
          Validators.required,
          Validators.min(0.1),
          Validators.max(this.selectedLotItem()?.netQuantity || 0),
          this.netQuantityValidator.bind(this),
        ],
      ],
      packagingType: [initialData?.packagingType, Validators.required],
    });
    form.get('originalGrossQuantity')?.valueChanges.subscribe(() => {
      form.get('originalNetQuantity')?.updateValueAndValidity({ emitEvent: false });
    });

    form.get('originalNetQuantity')?.valueChanges.subscribe(() => {
      form.get('originalGrossQuantity')?.updateValueAndValidity({ emitEvent: false });
    });
    return form;
  }

  addSplitLotItem() {
    if (!this.selectedLotItem()) {
      return;
    }

    const newSplitItemForm = this.createSplitItemForm({
      originalGrossQuantity: 0,
      originalNetQuantity: 0,
    });

    this.splitItemsForms.update((forms) => [...forms, newSplitItemForm]);
  }

  removeSplitLotItem(index: number) {
    if (index >= 0 && index < this.splitItemsForms().length) {
      this.splitItemsForms.update((forms) => forms.filter((_, i) => i !== index));
    }
  }

  getSplitItemForm(index: number): FormGroup {
    return this.splitItemsForms()[index];
  }

  private validateTotals() {
    const originalQuantity = this.selectedLotItem()?.netQuantity || 0;
    const totalUsed = this.totalNetQuantity();

    if (totalUsed > originalQuantity) {
      console.warn('Total exceeds original quantity:', {
        original: originalQuantity,
        used: totalUsed,
      });
    }
  }

  save() {
    if (!this.canSave()) {
      return;
    }

    const splitItems = this.splitItemsValues()
      .filter((item) => !item.id)
      .map((item) => ({
        originalGrossQuantity: Number(item.originalNetQuantity), //this line was removed in the template but this to take the values of item.originalNetQuantity
        originalNetQuantity: Number(item.originalNetQuantity),
        packagingType: Number(item.packagingType),
      }));

    if (splitItems.length > 0) {
      this.createSplitLotItem(splitItems);
    }
  }

  createSplitLotItem(request: SplitLotItem[]) {
    this.lotItemProxyService
      .splitLotItem(request, this.splitLotItemId())
      .pipe(
        finalize(() => {
          this.stopLoading();
        }),
      )
      .subscribe({
        next: (response) => {
          if (!_.isNil(response.data) && !_.isEmpty(response.data)) {
            const prev = this.existingSplitLotItem();
            this.existingSplitLotItem.set([...prev, ...response.data!]);
            if (this.splitItemsForms().length > 0) {
              this.splitItemsForms.set([]);
            }
            this.getLotItemById(this.currentLotItemSelected);
          }
        },
      });
  }

  // Method to cleanup subscriptions
  private cleanupSubscriptions() {
    this.valueChangeSubscriptions.forEach((sub) => {
      if (sub && typeof sub.unsubscribe === 'function') {
        sub.unsubscribe();
      }
    });
    this.valueChangeSubscriptions = [];
  }

  ngOnDestroy() {
    this.cleanupSubscriptions();
  }

  private _setOptionsForPrimeNg(value: Record<string, string>) {
    return Object.keys(value).map((key) => {
      return {
        label: value[key],
        value: key,
      };
    });
  }

  printLotItem(lotItemId: string) {
    this.lotItemProxyService.getLotItemLabel(lotItemId!).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      },
    });
  }

  getPackagingTypeLabel(packagingTypeValue: string | number): string {
    const option = this.packagingTypeOptions.find((opt) => opt.value === String(packagingTypeValue));
    return option ? option.label : String(packagingTypeValue);
  }

  private grossQuantityValidator(control: AbstractControl): ValidationErrors | null {
    const formGroup = control.parent as FormGroup;
    if (!formGroup) return null;

    const grossQuantity = control.value;
    const netQuantity = formGroup.get('originalNetQuantity')?.value;

    if (grossQuantity && netQuantity && parseFloat(grossQuantity) < parseFloat(netQuantity)) {
      return { grossLessThanNet: true };
    }

    return null;
  }

  private netQuantityValidator(control: AbstractControl): ValidationErrors | null {
    const formGroup = control.parent as FormGroup;
    if (!formGroup) return null;

    const netQuantity = control.value;
    const grossQuantity = formGroup.get('originalGrossQuantity')?.value;

    if (grossQuantity && netQuantity && parseFloat(netQuantity) > parseFloat(grossQuantity)) {
      return { netGreaterThanGross: true };
    }

    return null;
  }

  getLotItemById(id: string) {
    this.splitLotItemId.set(id);

    this.lotItemProxyService.getLotItemById(id).subscribe({
      next: (response) => {
        this.selectedLotItem.set(response.data);

        if (response.data) {
          const lotItem = response.data;

          this.loadItemMaster(lotItem.itemCode);

          const alreadySplitLotIds = lotItem.consumptionEntries
            .filter((entry) => entry.initiator.entityDefinitionCode === 1)
            .map((entry) => entry.initiator.reference);

          if (alreadySplitLotIds.length > 0) {
            this.loadExistingSplitLotItem(alreadySplitLotIds);
          } else {
            this.existingSplitLotItem.set([]);
          }
        }
      },
    });
  }

  private loadItemMaster(itemCode: string) {
    this.selectedItemMaster.set(null);
    if (!itemCode) return;

    this.itemMasterProxyService.getByItemCodes([itemCode]).subscribe({
      next: (response) => {
        this.selectedItemMaster.set(response.data?.[0] ?? null);
      },
    });
  }

  canEdit() {
    // if Admin or Editor
    if (AuthService.hasRole(UserGroup.SplitLotItemAdmin)) {
      return true;
    }
    // if Viewer
    if (AuthService.hasRole(UserGroup.SplitLotItemViewer)) {
      return false;
    }
    // Default
    return false;
  }
}
